/**
 * Profil — der Aufbau aus `templates/profile-settings/ProfileSettings.dc.html`
 * des Design-Systems.
 *
 * **Übernommen ist der Aufbau, nicht der Inhalt.** Dieselbe Regel wie bei
 * HM-030: Von den Angaben der Vorlage gibt es Vor- und Nachname (seit HM-033)
 * und sonst wenig — Telefonnummer, bestätigte E-Mail-Adresse,
 * Zwei-Faktor-Anmeldung, Benachrichtigungen, Zeitformat, hell/dunkel,
 * Datenexport und Kontolöschung haben keine Datengrundlage. Die **aktiven
 * Sitzungen** gibt es seit HM-101 als eigene Karte. Ein
 * Feld, das man ausfüllt und dessen „Speichern" nirgendwohin führt, ist
 * schlimmer als ein fehlendes; was es nicht gibt, wird ausgeblendet und nicht
 * ausgegraut (Regel 10). Am Ende der Seite steht stattdessen ein Absatz, der
 * das benennt.
 *
 * Ebenfalls nicht übernommen: das Kachelraster „Meine Sites" für die
 * Zugehörigkeiten. Ein Nutzer gehört heute zu genau **einem** Account — ein
 * Raster mit einer Kachel ist eine Liste, die so tut, als gäbe es eine Auswahl.
 * Accountname und Rolle stehen deshalb als zwei Angaben unter „Konto".
 *
 * Was von der Vorlage bleibt, ist ihre Ordnung:
 *
 * 1. **Kopfkarte** — großes Bild, Name, darunter die Kennung in einer Zeile,
 *    rechts die Aktionen dazu.
 * 2. **Abschnittskarten** mit Überschrift in der Anzeigeschrift, darin Felder
 *    im Raster bzw. Zeilen aus Beschriftung links und Aktion rechts.
 *
 * Zwei Dinge, die man beim Bild sonst falsch macht: Es wird **im Browser**
 * verkleinert (ADR 0016) und mit `fetch` geladen statt über `<img src>` — ein
 * Bildaufruf des Browsers trägt keinen `Authorization`-Kopf. Beides steht in
 * `lib/avatar.ts`.
 *
 * Und beim Passwort: **Die Änderung entwertet jede Sitzung**, auch die eigene;
 * die Antwort trägt deshalb ein neues Tokenpaar, das `changePassword` in
 * `lib/session.ts` sofort übernimmt.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Alert, Badge, Button, Card, Dialog, Input, Select, Switch, icons } from "@hausmeter/ui";
import { useRef, useState, type FormEvent, type ReactNode } from "react";
import type { ApiComponents } from "@hausmeter/sdk";
import { api } from "../lib/client.js";
import { apiErrorMessage } from "../lib/api-error.js";
import { ACCEPTED_IMAGE_TYPES, prepareAvatar, useAvatarUrl } from "../lib/avatar.js";
import { displayName, formatDateTime, formatMonthYear, formatTimeAgo, initialsOf } from "../lib/format.js";
import { navigate, routeFor, routePath } from "../lib/router.js";
import { ownsAccount, platformRoleLabel } from "../lib/platform-role.js";
import { deletionBlockedReason, deletionConsequences } from "../lib/account-deletion.js";
import { SharedSitesCard } from "../components/shared-sites-card.js";
import {
  accountDeletionPreview,
  changePassword,
  createOwnSite,
  deleteAccount,
  logout,
  replaceUser,
  resendVerification,
  type CurrentUser,
} from "../lib/session.js";
import {
  revokeOtherSessions,
  revokeSession,
  SESSIONS_QUERY_KEY,
  sessionClientLabel,
  useSessions,
  type Session,
} from "../lib/sessions.js";

type AccountRole = ApiComponents["schemas"]["AccountSummary"]["role"];
type Salutation = ApiComponents["schemas"]["Salutation"];

/**
 * Die Anrede in Worten. Die Werte stammen aus `SALUTATIONS` in
 * `@hausmeter/domain` — hier steht die Übersetzung, nicht eine zweite Liste.
 *
 * **„Keine Angabe" ist kein Eintrag dieser Tabelle**, sondern der leere Wert des
 * Auswahlfelds: Im Vertrag ist das `null`, und zwei Arten von Nichts wären eine
 * zu viel.
 */
const SALUTATION_LABELS: Record<Salutation, string> = {
  ms: "Frau",
  mr: "Herr",
  diverse: "Divers",
};

/** Die Kontorolle in Worten — was jemand *in einem Account* darf. */
const ACCOUNT_ROLE_LABELS: Record<AccountRole, string> = {
  owner: "Eigentümer",
  admin: "Administrator",
  member: "Mitglied",
  viewer: "Betrachter",
  resident: "Bewohner",
  installer: "Installateur",
  guest: "Gast an einer geteilten Anlage",
};

/**
 * Die Vorlage setzt den Inhalt auf 900 px, obwohl die Shell mehr hergäbe.
 * Zurecht: Hier steht Fließtext und stehen Formulare, keine Kacheln. Eine Zeile
 * über die volle Breite eines großen Bildschirms liest sich schlecht, und ein
 * Knopf am rechten Rand ist weit weg von der Beschriftung, zu der er gehört.
 */
const CONTENT_WIDTH = 900;

export function ProfileScreen({ user }: { user: CurrentUser }) {
  return (
    <div
      style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: CONTENT_WIDTH }}
    >
      <IdentityCard user={user} />
      <PersonalDataCard user={user} />
      <SecurityCard />
      <SessionsCard />
      <SharedSitesCard />
      <UsageAnalyticsCard user={user} />
      <AccountCard user={user} />
      {!ownsAccount(user.accounts) && <OwnSiteCard user={user} />}
      <DeleteAccountCard />
    </div>
  );
}

/**
 * „Persönliche Daten" der Vorlage — mit den beiden Feldern, die es gibt.
 *
 * **Beide sind freiwillig und dürfen leer bleiben.** Kein `required`: HausMeter
 * misst Strom; wer seinen Zähler ablesen will, muss nicht sagen, wie er heißt.
 * Ein geleertes Feld schickt deshalb `null` und löscht den Eintrag — die leere
 * Zeichenkette wäre kein Name, sondern ein Name aus null Zeichen.
 *
 * Die E-Mail-Adresse steht hier **nicht**, obwohl die Vorlage sie zeigt: Sie ist
 * die Anmeldung, und sie zu verlegen braucht einen Nachweis an der neuen
 * Adresse (HM-026). Ein Feld dafür zwischen Vor- und Nachname sähe aus wie eine
 * Kleinigkeit.
 */
function PersonalDataCard({ user }: { user: CurrentUser }) {
  return (
    <PersonalDataForm
      key={`${user.salutation}|${user.firstName}|${user.lastName}`}
      user={user}
    />
  );
}

function PersonalDataForm({ user }: { user: CurrentUser }) {
  /* Der leere Wert steht für „keine Angabe" und wird als `null` geschickt. */
  const [salutation, setSalutation] = useState<Salutation | "">(user.salutation ?? "");
  const [firstName, setFirstName] = useState(user.firstName ?? "");
  const [lastName, setLastName] = useState(user.lastName ?? "");
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const { data, error: failed } = await api.PATCH("/auth/me", {
        body: {
          salutation: salutation === "" ? null : salutation,
          firstName: trimmedOrNull(firstName),
          lastName: trimmedOrNull(lastName),
        },
      });
      if (failed || !data) throw new Error(apiErrorMessage(failed) ?? "Speichern fehlgeschlagen.");
      return data;
    },
    onSuccess: (updated) => {
      setError(null);
      // Der ganze Nutzer zurück in die Sitzung: Danach trägt auch die Kontozeile
      // der Seitenleiste den neuen Namen, ohne dass jemand neu lädt.
      replaceUser(updated);
    },
    onError: (cause: Error) => setError(cause.message),
  });

  const unchanged =
    (salutation === "" ? null : salutation) === user.salutation &&
    trimmedOrNull(firstName) === user.firstName &&
    trimmedOrNull(lastName) === user.lastName;

  function submit(event: FormEvent): void {
    event.preventDefault();
    save.mutate();
  }

  return (
    <Card
      title="Persönliche Daten"
      subtitle="Freiwillig. Ohne Namen erscheint deine E-Mail-Adresse."
      padding="lg"
    >
      <form onSubmit={submit}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 210px), 1fr))",
            gap: 12,
          }}
        >
          <Select
            label="Anrede"
            value={salutation}
            /* Der erste Eintrag ist der leere: Ohne ihn müsste jeder, der die
               Anrede wieder loswerden will, eine falsche auswählen. */
            options={[
              { value: "", label: "Keine Angabe" },
              ...Object.entries(SALUTATION_LABELS).map(([value, label]) => ({ value, label })),
            ]}
            onChange={(event) => setSalutation(event.target.value as Salutation | "")}
          />
          <Input
            label="Vorname"
            value={firstName}
            maxLength={80}
            autoComplete="given-name"
            onChange={(event) => setFirstName(event.target.value)}
          />
          <Input
            label="Nachname"
            value={lastName}
            maxLength={80}
            autoComplete="family-name"
            onChange={(event) => setLastName(event.target.value)}
          />
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
          {/* „Verwerfen" wie im Kit — es setzt auf den gespeicherten Stand
              zurück und ist deshalb nur dann sichtbar, wenn es etwas zu
              verwerfen gibt. */}
          {!unchanged && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSalutation(user.salutation ?? "");
                setFirstName(user.firstName ?? "");
                setLastName(user.lastName ?? "");
                setError(null);
              }}
            >
              Verwerfen
            </Button>
          )}
          <Button type="submit" size="sm" disabled={save.isPending || unchanged}>
            Speichern
          </Button>
        </div>
      </form>

      {error !== null && (
        <div style={{ marginTop: 12 }}>
          <Alert>{error}</Alert>
        </div>
      )}
    </Card>
  );
}

/**
 * Die Kopfkarte: Bild, Kennung, Aktionen — die erste `<section>` der Vorlage.
 *
 * Wie dort trägt der **Name** die Hauptzeile und die E-Mail-Adresse steht
 * darunter. Fehlt der Name — der Normalfall, beide Felder sind freiwillig —,
 * rückt die E-Mail-Adresse nach oben; eine Zeile „Kein Name hinterlegt" wäre
 * eine Beschwerde über etwas, das niemand versprochen hat.
 */
function IdentityCard({ user }: { user: CurrentUser }) {
  const url = useAvatarUrl(user.avatarUpdatedAt);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const prepared = await prepareAvatar(file);
      const { data, error: failed } = await api.PUT("/auth/me/avatar", { body: prepared });
      if (failed || !data) throw new Error(apiErrorMessage(failed) ?? "Hochladen fehlgeschlagen.");
      return data;
    },
    // Der Endpunkt gibt den ganzen Nutzer zurück; ihn zu übernehmen ändert den
    // Zeitstempel und stößt damit das Nachladen des Bildes an.
    onSuccess: (updated) => {
      setError(null);
      replaceUser(updated);
    },
    onError: (cause: Error) => setError(cause.message),
  });

  const remove = useMutation({
    mutationFn: async () => {
      const { data, error: failed } = await api.DELETE("/auth/me/avatar", {});
      if (failed || !data) throw new Error(apiErrorMessage(failed) ?? "Entfernen fehlgeschlagen.");
      return data;
    },
    onSuccess: (updated) => {
      setError(null);
      replaceUser(updated);
    },
    onError: (cause: Error) => setError(cause.message),
  });

  const busy = upload.isPending || remove.isPending;
  const initials = initialsOf(user);

  return (
    <Card padding="lg">
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 16 }}>
        {/* Kit: 56 px, vollrund. `overflow: hidden` gehört zum Radius — er
            rundet den Rahmen, beschneidet aber nichts darin. */}
        <span
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 56,
            height: 56,
            flex: "none",
            borderRadius: "var(--radius-pill)",
            overflow: "hidden",
            background: "var(--hm-green-100)",
            color: "var(--hm-green-700)",
            fontFamily: "var(--font-display)",
            fontWeight: 800,
            fontSize: 20,
          }}
        >
          {url !== null ? (
            <img
              src={url}
              alt="Dein Profilbild"
              width={56}
              height={56}
              style={{ objectFit: "cover" }}
            />
          ) : (
            /* Ohne Bild die Initialen, wie im Kit das „AB" — und ohne Namen das
               gezeichnete Symbol. Aus einer E-Mail-Adresse Buchstaben zu ziehen
               wäre geraten: `info@…` ergäbe ein „I" über niemanden. */
            (initials ?? <icons.IconUser size={26} />)
          )}
        </span>

        <span
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            flex: "1 1 200px",
            minWidth: 0,
          }}
        >
          <span
            style={{
              fontFamily: "var(--font-display)",
              fontWeight: 800,
              fontSize: 18,
              letterSpacing: "var(--tracking-tight)",
              color: "var(--text-strong)",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {displayName(user)}
          </span>
          <span style={{ fontSize: 13, color: "var(--text-muted)" }}>
            {/* Kit: „anna.berger@example.de · Mitglied seit März 2024". Steht der
                Name oben nicht, ist die Adresse schon die Hauptzeile und würde
                sich hier wiederholen. */}
            {hasName(user) ? `${user.email} · ` : ""}
            {platformRoleLabel(user.platformRole)} · Mitglied seit{" "}
            {formatMonthYear(user.createdAt)}
          </span>
        </span>

        <span style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {/* Das Feld selbst bleibt verborgen: Ein nacktes `input[type=file]`
              lässt sich nicht gestalten und sähe in jedem Browser anders aus.
              Bedient wird es über den Knopf — mit einem echten Klick. */}
          <input
            ref={fileInput}
            type="file"
            accept={ACCEPTED_IMAGE_TYPES}
            style={{ display: "none" }}
            onChange={(event) => {
              const file = event.target.files?.[0];
              // Zurücksetzen, damit dieselbe Datei ein zweites Mal ein
              // `change`-Ereignis auslöst — sonst bliebe ein Fehlversuch stumm.
              event.target.value = "";
              if (file) upload.mutate(file);
            }}
          />
          <Button
            variant="secondary"
            size="sm"
            disabled={busy}
            onClick={() => fileInput.current?.click()}
          >
            {user.avatarUpdatedAt === null ? "Bild wählen" : "Bild ändern"}
          </Button>
          {user.avatarUpdatedAt !== null && (
            <Button variant="ghost" size="sm" disabled={busy} onClick={() => remove.mutate()}>
              Entfernen
            </Button>
          )}
        </span>
      </div>

      <p style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: 12 }}>
        PNG, JPEG oder WebP.
      </p>

      {error !== null && (
        <div style={{ marginTop: 12 }}>
          <Alert>{error}</Alert>
        </div>
      )}
    </Card>
  );
}

/**
 * „Anmeldung & Sicherheit" aus der Vorlage — mit **einer** der drei Zeilen.
 *
 * Dort stehen Passwort, Zwei-Faktor-Anmeldung und die Liste aktiver Sitzungen.
 * Die zweite hat keinen Endpunkt (`users.mfa_method` steht auf `none` und wird
 * von nichts gesetzt) — ein Schalter, der nichts schaltet, wäre schlimmer als
 * seine Abwesenheit. Die dritte ist seit HM-101 die Karte darunter: Sie ist
 * eine Liste mit Handlungen und keine Zeile, deshalb hat sie ihre eigene.
 *
 * Auch das „Zuletzt geändert am 14. Januar 2026" der Vorlage fehlt: Wann ein
 * Passwort zuletzt gewechselt wurde, steht nirgends.
 */
function SecurityCard() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [repeat, setRepeat] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  /**
   * Die Wiederholung prüft nur der Client — der Server sieht sie nie. Sie ist
   * kein Sicherheitsmerkmal, sondern ein Schutz vor dem Vertippen: Ein falsch
   * eingegebenes neues Passwort fiele sonst erst beim nächsten Anmelden auf,
   * und dann kommt man nicht mehr herein.
   */
  const mismatch = repeat.length > 0 && next !== repeat;
  const tooShort = next.length > 0 && next.length < 12;

  const change = useMutation({
    mutationFn: async () => changePassword({ currentPassword: current, newPassword: next }),
    onSuccess: () => {
      // Schließen **und** leeren: Ein Dialog, der nach dem Erfolg offen bleibt,
      // sieht aus, als sei nichts passiert. Die Bestätigung steht danach auf
      // der Karte dahinter.
      close();
      setDone(true);
    },
    onError: (cause: Error) => {
      setDone(false);
      setError(cause.message);
    },
  });

  /** Schließt und räumt auf — Passwörter bleiben nicht in einem Formular liegen. */
  function close(): void {
    setOpen(false);
    setCurrent("");
    setNext("");
    setRepeat("");
    setError(null);
  }

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (mismatch || tooShort) return;
    change.mutate();
  }

  return (
    <>
      <Card title="Anmeldung und Sicherheit" padding="lg">
        <SettingRow
          title="Passwort"
          description="Mindestens zwölf Zeichen. Nach einer Änderung sind alle anderen angemeldeten Geräte abgemeldet; hier bleibst du angemeldet."
          action={
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setDone(false);
                setOpen(true);
              }}
            >
              Ändern
            </Button>
          }
        />

        {done && (
          <div style={{ marginTop: 12 }}>
            {/* `info` und nicht `success`: Den Ton gibt es im Design-System
                nicht, und ihn für eine einzelne Meldung zu erfinden, hieße das
                System an der Anwendung vorbei zu erweitern. */}
            <Alert tone="info">
              Das Passwort ist geändert. Andere Geräte müssen sich neu anmelden.
            </Alert>
          </div>
        )}
      </Card>

      <Dialog
        open={open}
        onClose={close}
        title="Passwort ändern"
        description="Das bisherige Passwort weist dich als Kontoinhaber aus — ein angemeldetes Gerät allein tut das nicht."
        footer={
          <>
            <Button variant="ghost" onClick={close}>
              Abbrechen
            </Button>
            {/* Der Knopf liegt außerhalb des `<form>` und findet es über `form`.
                Ohne das müsste die Fußzeile ins Formular wandern — und der Fuß
                gehört dem Dialog, nicht dem Inhalt. */}
            <Button
              type="submit"
              form={FORM_ID}
              disabled={change.isPending || mismatch || tooShort}
            >
              Passwort ändern
            </Button>
          </>
        }
      >
        <form id={FORM_ID} onSubmit={submit}>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
            <Input
              label="Bisheriges Passwort"
              type="password"
              autoComplete="current-password"
              value={current}
              required
              onChange={(event) => setCurrent(event.target.value)}
            />
            <Input
              label="Neues Passwort"
              type="password"
              autoComplete="new-password"
              value={next}
              required
              minLength={12}
              error={tooShort ? "Mindestens zwölf Zeichen." : undefined}
              onChange={(event) => setNext(event.target.value)}
            />
            <Input
              label="Neues Passwort wiederholen"
              type="password"
              autoComplete="new-password"
              value={repeat}
              required
              error={mismatch ? "Die beiden Eingaben stimmen nicht überein." : undefined}
              onChange={(event) => setRepeat(event.target.value)}
            />
          </div>

          {error !== null && (
            <div style={{ marginTop: "var(--sp-3)" }}>
              <Alert>{error}</Alert>
            </div>
          )}
        </form>
      </Dialog>
    </>
  );
}

/**
 * „Persönliche Daten" und „Darstellung" der Vorlage, zusammengezogen auf das,
 * was es gibt.
 *
 * Dort stehen sie als **Felder** — Kästen mit Rahmen, die zum Hineinschreiben
 * einladen. Hier stehen dieselben Angaben als Werte ohne Rahmen: Ändern lässt
 * sich keine davon. Ein Feld, das aussieht wie ein Feld und keines ist, ist die
 * unehrlichste Form von Anzeige.
 */
function AccountCard({ user }: { user: CurrentUser }) {
  /**
   * Der erste Account — es ist heute immer genau einer. Das Kachelraster „Meine
   * Sites" aus der Vorlage ist deshalb weggefallen: Eine Liste mit einem
   * Eintrag tut so, als gäbe es eine Auswahl. Sobald jemand mehreren Accounts
   * angehören kann (HM-017), gehört es zurück.
   */
  const account = user.accounts[0];

  return (
    <Card title="Konto" subtitle="Angaben zum angemeldeten Nutzer." padding="lg">
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 210px), 1fr))",
          gap: 12,
        }}
      >
        <Fact label="E-Mail-Adresse" value={user.email} />
        <Fact label="Kontoart" value={platformRoleLabel(user.platformRole)} />
        <Fact label="Anlagen" value={quotaLabel(user.siteQuota)} />
        <Fact label="Sprache" value={languageLabel(user.locale)} />
        {account !== undefined && (
          <>
            <Fact label="Kontoname" value={account.name} />
            <Fact label="Deine Rolle" value={ACCOUNT_ROLE_LABELS[account.role]} />
          </>
        )}
      </div>

      {user.emailVerifiedAt === null && <UnverifiedNotice />}

      {/* Der Satz stammt aus der Vorlage und trifft hier genauso: Zeitzone und
          Anschrift hängen an der Anlage, nicht am Konto. */}
      <p style={{ fontSize: 12.5, lineHeight: 1.5, color: "var(--text-muted)", marginTop: 12 }}>
        Zeitzone, Anschrift und Netzbetreiber gehören zur jeweiligen Anlage und werden dort
        unter „Einstellungen" gepflegt.
      </p>
    </Card>
  );
}

/**
 * Die erste eigene Anlage (HM-116, ADR 0035).
 *
 * **Nur für einen Nutzer ohne eigenen Account** — in der Regel ein Bewohner.
 * Wer einen hat, legt weitere Anlagen im Umschalter an; eine zweite Stelle
 * dafür im Profil wäre ein Weg, der je nach Konto mal geht und mal `409` sagt.
 *
 * **Der Name der Anlage steht im Dialog**, weil mit dem Klick alles auf einmal
 * entsteht: Account, Anlage, Standardkonto. Ein Rollenwechsel ohne Anlage
 * hinterließe einen Standard-Nutzer, dessen „Anlage anlegen" nirgendwohin
 * führt. Und die Karte sagt vorher, dass der Weg nicht zurückführt — an der
 * Anlage hängen bald Zählerstände — und dass die Wohnung bleibt: Das ist die
 * Frage, die sich ein Mieter an dieser Stelle zuerst stellt.
 */
function OwnSiteCard({ user }: { user: CurrentUser }) {
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const resident = user.platformRole === "resident";

  const create = useMutation({
    mutationFn: async () => createOwnSite(name.trim()),
    onSuccess: async (siteId) => {
      close();
      await client.invalidateQueries({ queryKey: ["sites"] });
      navigate(routeFor("electricity", siteId));
    },
    onError: (cause: Error) => setError(cause.message),
  });

  function close(): void {
    setOpen(false);
    setName("");
    setError(null);
  }

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (name.trim().length === 0) return;
    create.mutate();
  }

  return (
    <>
      <Card title="Eigene Anlage" padding="lg">
        <SettingRow
          title={resident ? "Zum Standardkonto wechseln" : "Anlage anlegen"}
          description={
            resident
              ? "Dein Konto ist ein Bewohnerkonto: Du siehst deine Wohnung oder die Anlagen, die mit dir geteilt sind, eine eigene Anlage führt es nicht. Mit einer eigenen Anlage wird es zum Standardkonto — was du heute siehst, siehst du weiterhin."
              : "Zu deinem Konto gehört noch keine eigene Anlage. Was du heute siehst, siehst du weiterhin."
          }
          action={
            <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
              Anlage anlegen …
            </Button>
          }
        />
      </Card>

      <Dialog
        open={open}
        onClose={close}
        title="Eigene Anlage anlegen"
        description={
          resident
            ? "Dein Konto wird dabei zum Standardkonto und führt genau eine eigene Anlage. Zurück zum Bewohnerkonto geht es danach nicht mehr."
            : "Die Anlage entsteht in einem eigenen Account, den du besitzt."
        }
        footer={
          <>
            <Button variant="ghost" onClick={close}>
              Abbrechen
            </Button>
            <Button type="submit" form={OWN_SITE_FORM_ID} disabled={create.isPending || name.trim().length === 0}>
              Anlage anlegen
            </Button>
          </>
        }
      >
        <form id={OWN_SITE_FORM_ID} onSubmit={submit}>
          <Input
            label="Name der Anlage"
            value={name}
            required
            maxLength={120}
            hint="Zum Beispiel „Haus am Hang“. Zeitzone, Art und Anschrift folgen in den Einstellungen der Anlage."
            onChange={(event) => setName(event.target.value)}
          />
          {error !== null && (
            <div style={{ marginTop: "var(--sp-3)" }}>
              <Alert>{error}</Alert>
            </div>
          )}
        </form>
      </Dialog>
    </>
  );
}

/**
 * Der Hinweis auf eine unbestätigte Adresse (HM-026).
 *
 * **Er sperrt nichts und behauptet keinen Fehler.** Ein unbestätigtes Konto ist
 * vollständig nutzbar (OQ-017); was fehlt, ist die Gewissheit, dass ein
 * vergessenes Passwort wieder herzustellen ist — und genau das steht da. Ein
 * roter Kasten an dieser Stelle wäre eine Warnung ohne Schaden dahinter, und
 * wer die einmal zu Unrecht gesehen hat, glaubt der nächsten nicht mehr.
 *
 * Er verschwindet nach dem Bestätigen von selbst: `emailVerifiedAt` kommt aus
 * `GET /auth/me`, und das Bestätigen ersetzt den Nutzer im Sitzungszustand.
 */
/**
 * „Deutsch (Deutschland)" statt „de-DE" — der Code ist eine Angabe für Programme.
 * Kennt der Browser ihn nicht, bleibt er stehen statt zu verschwinden.
 */
function languageLabel(locale: string): string {
  try {
    return new Intl.DisplayNames(["de"], { type: "language" }).of(locale) ?? locale;
  } catch {
    return locale;
  }
}

function UnverifiedNotice() {
  const [state, setState] = useState<"ruhe" | "laeuft" | "gesendet" | "fehler">("ruhe");
  const [error, setError] = useState<string | null>(null);

  async function send(): Promise<void> {
    setState("laeuft");
    setError(null);
    try {
      await resendVerification();
      setState("gesendet");
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause.message : "Unbekannter Fehler.");
      setState("fehler");
    }
  }

  return (
    <div
      style={{
        marginTop: 12,
        padding: "10px 12px",
        borderRadius: "var(--radius-md)",
        background: "var(--surface-sunken)",
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 10,
      }}
    >
      <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.5, color: "var(--text-muted)" }}>
        {state === "gesendet"
          ? "Die Nachricht ist unterwegs. Sieh auch im Spam-Ordner nach."
          : "Deine E-Mail-Adresse ist noch nicht bestätigt. Erst damit kannst du ein " +
            "vergessenes Passwort selbst zurücksetzen."}
      </p>
      {state !== "gesendet" && (
        <Button size="sm" variant="ghost" disabled={state === "laeuft"} onClick={() => void send()}>
          {state === "laeuft" ? "Einen Moment …" : "Bestätigung senden"}
        </Button>
      )}
      {error !== null && (
        <p style={{ margin: 0, fontSize: 12.5, color: "var(--text-muted)" }}>{error}</p>
      )}
    </div>
  );
}

/**
 * „Angemeldete Geräte" (HM-101, ADR 0029): je Anmeldung eine Zeile, die eigene
 * zuerst und markiert; jede andere lässt sich beenden, alle anderen auf einmal
 * auch.
 *
 * Vier Dinge daran:
 *
 * **Das Gerät heißt, was der Server erkannt hat — oder es heißt nichts.** Aus
 * der Browserkennung kommt „Safari auf iPhone"; kam nichts Erkennbares, steht
 * „Gerät nicht erkannt" und kein erfundener Name. Eine Zeile, die „Unbekanntes
 * Gerät" hieße, sähe aus wie ein fremder Zugriff (Regel 9).
 *
 * **Die eigene Sitzung wird abgemeldet, nicht beendet.** Der Knopf heißt so
 * und tut, was „Abmelden" im Kontomenü tut — der Weg über `DELETE` ginge auch,
 * ließe die Anwendung aber mit einem toten Zustand zurück, bis die nächste
 * Anfrage `401` bekommt.
 *
 * **Beenden ist keine Rückfrage wert.** Das andere Gerät meldet sich neu an,
 * mehr passiert nicht; ein Dialog davor wäre die Hürde vor einer Handlung,
 * für die man die Seite geöffnet hat. Ohne die drei Punkte, weil keine
 * Rückfrage kommt (HM-042).
 *
 * **„Zuletzt aktiv" ist auf eine Viertelstunde genau.** Der Server sieht ein
 * Gerät nur, wenn dessen Token rotiert, und das geschieht alle vierzehn
 * Minuten. Ein „vor 3 Minuten" ist deshalb kein Beleg dafür, dass gerade
 * jemand tippt — und der Satz sagt es dazu.
 */
/** So viele Anmeldungen stehen sichtbar da, der Rest hinter „weitere anzeigen“. */
const VISIBLE_SESSIONS = 5;

function SessionsCard() {
  const queryClient = useQueryClient();
  const sessions = useSessions();
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: SESSIONS_QUERY_KEY });

  const revokeOne = useMutation({
    mutationFn: revokeSession,
    onSuccess: () => {
      setError(null);
      void invalidate();
    },
    onError: (cause: Error) => setError(cause.message),
  });

  const revokeOthers = useMutation({
    mutationFn: revokeOtherSessions,
    onSuccess: () => {
      setError(null);
      void invalidate();
    },
    onError: (cause: Error) => setError(cause.message),
  });

  const list = sessions.data?.sessions ?? [];
  const others = list.filter((session) => !session.current);
  /* Wer viel unterwegs ist, hat schnell zehn Anmeldungen; die Liste zeigt die
     jüngsten und klappt den Rest ein (UI-Durchsicht, U13). */
  const [showAll, setShowAll] = useState(false);
  const shown = showAll ? list : list.slice(0, VISIBLE_SESSIONS);

  return (
    <Card title="Angemeldete Geräte" padding="lg">
      <p style={{ margin: "0 0 12px", fontSize: 13, lineHeight: 1.55, color: "var(--text-muted)" }}>
        Ein Gerät, das du nicht wiedererkennst, meldest du hier ab. Gespeichert ist nur die Art
        des Browsers und des Systems — keine Adresse, kein Ort.
      </p>

      {sessions.isPending && (
        <p style={{ margin: 0, fontSize: 13, color: "var(--text-faint)" }}>Wird geladen …</p>
      )}
      {sessions.isError && <Alert>{sessions.error.message}</Alert>}

      {shown.map((session) => (
        <SessionRow
          key={session.id}
          session={session}
          busy={revokeOne.isPending && revokeOne.variables === session.id}
          onRevoke={() => revokeOne.mutate(session.id)}
        />
      ))}
      {list.length > shown.length && (
        <Button variant="ghost" size="sm" onClick={() => setShowAll(true)}>
          {list.length - shown.length} weitere anzeigen
        </Button>
      )}

      {others.length > 0 && (
        <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="secondary"
            size="sm"
            disabled={revokeOthers.isPending}
            onClick={() => revokeOthers.mutate()}
          >
            {others.length === 1 ? "Das andere Gerät abmelden" : "Alle anderen Geräte abmelden"}
          </Button>
        </div>
      )}

      {error !== null && (
        <div style={{ marginTop: 12 }}>
          <Alert>{error}</Alert>
        </div>
      )}
    </Card>
  );
}

function SessionRow({
  session,
  busy,
  onRevoke,
}: {
  readonly session: Session;
  readonly busy: boolean;
  readonly onRevoke: () => void;
}) {
  const label = sessionClientLabel(session.client);
  const facts = [
    `angemeldet seit ${formatDateTime(session.startedAt)}`,
    `zuletzt aktiv ${formatTimeAgo(session.lastActiveAt)}`,
    session.rememberMe ? "bleibt angemeldet" : "bis zum Schließen des Browsers",
  ];

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 12,
        alignItems: "center",
        justifyContent: "space-between",
        padding: "10px 0",
        borderTop: "1px solid var(--border-subtle)",
      }}
    >
      <div style={{ minWidth: 0, flex: "1 1 240px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: "var(--text-strong)" }}>
            {label ?? "Gerät nicht erkannt"}
          </span>
          {session.current && <Badge tone="brand">Dieses Gerät</Badge>}
        </div>
        <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
          {facts.join(" · ")}
        </div>
      </div>
      {session.current ? (
        <Button variant="ghost" size="sm" onClick={() => void logout()}>
          Abmelden
        </Button>
      ) : (
        <Button variant="secondary" size="sm" disabled={busy} onClick={onRevoke}>
          Beenden
        </Button>
      )}
    </div>
  );
}

/**
 * Was die Vorlage zeigt und es hier nicht gibt.
 *
 * Kein „HM-026" im sichtbaren Text: Eine Slice-Nummer sagt dem Nutzer nichts
 * und wirkt wie ein Fehlercode. Was fehlt, steht als Satz — und es steht
 * überhaupt da, weil eine Seite, der sichtbar Abschnitte fehlen, sonst
 * unfertig statt ehrlich aussieht.
 */
/**
 * Die Abwahl der Nutzungsstatistik (HM-168, ADR 0046).
 *
 * **Ein Schalter, der sofort gilt**, kein Formular mit Speichern: Es gibt nichts
 * abzuwägen, und wer abwählt, will nicht, dass bis zum Klick auf „Speichern"
 * weiter gezählt wird. Er gilt für das **Konto** — Web-App und App lesen
 * denselben Wert —, und die API speichert danach nichts mehr, was ein Client
 * noch schickt.
 */
function UsageAnalyticsCard({ user }: { user: CurrentUser }) {
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: async (enabled: boolean) => {
      const { data, error: failed } = await api.PATCH("/auth/me", { body: { usageAnalytics: enabled } });
      if (failed || !data) throw new Error(apiErrorMessage(failed) ?? "Speichern fehlgeschlagen.");
      return data;
    },
    onSuccess: (updated) => {
      setError(null);
      replaceUser(updated);
    },
    onError: (cause: Error) => setError(cause.message),
  });

  return (
    <Card title="Nutzungsstatistik" padding="lg">
      <SettingRow
        title="Nutzung anonym mitzählen"
        description="Gezählt wird, welche Ansichten und Funktionen von Web-App und App benutzt werden — je Tag, ohne deinen Namen und ohne deine Anlage. Deine Kennung wechselt jeden Tag; wer etwas getan hat, lässt sich daraus nicht ablesen. Die Zahlen helfen uns zu entscheiden, was wir verbessern. Gilt für Web-App und App."
        action={
          <Switch
            label="Nutzung mitzählen"
            labelHidden
            checked={save.isPending ? (save.variables ?? user.usageAnalytics) : user.usageAnalytics}
            disabled={save.isPending}
            onChange={(checked) => save.mutate(checked)}
          />
        }
      />
      <p style={{ fontSize: 12.5, lineHeight: 1.5, color: "var(--text-muted)", margin: "12px 0 0" }}>
        Einzelheiten stehen in der{" "}
        <a
          href={routePath({ kind: "legal", document: "privacy" })}
          onClick={(event) => {
            /* Strg-, Cmd- und Mittelklick öffnen wie gewohnt einen neuen Tab. */
            if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
            event.preventDefault();
            navigate({ kind: "legal", document: "privacy" });
          }}
        >
          Datenschutzerklärung
        </a>
        , Abschnitt 5b.
      </p>
      {error !== null && (
        <p style={{ fontSize: 12.5, color: "var(--hm-danger)", margin: "8px 0 0" }}>{error}</p>
      )}
    </Card>
  );
}

/**
 * Konto löschen — sofort und endgültig (HM-163, ADR 0044).
 *
 * **Die Rückfrage nennt, wen es außer Ihnen trifft**, und zwar mit Zahlen vom
 * Server: Dass die eigene Anlage weg ist, weiß jeder, der hier klickt; dass zwei
 * Mieter danach ihre Werte nicht mehr sehen, womöglich nicht. Geladen wird erst
 * beim Öffnen — die Auskunft zählt Bewohner und Gäste, und wer nur sein Profil
 * ansieht, braucht sie nicht.
 *
 * **Das Passwort steht im Dialog**, wie beim Passwortwechsel: Ein offenes Gerät
 * in fremder Hand soll ein Konto nicht mit zwei Klicks beenden. Nach dem
 * Löschen gibt es keine Sitzung mehr; die Anwendung zeigt die Anmeldung.
 */
function DeleteAccountCard() {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const preview = useMutation({ mutationFn: accountDeletionPreview });
  const remove = useMutation({
    mutationFn: async () => deleteAccount(password),
    onError: (cause: Error) => setError(cause.message),
  });

  function start(): void {
    setError(null);
    setPassword("");
    setOpen(true);
    preview.mutate();
  }

  function close(): void {
    setOpen(false);
    setPassword("");
    setError(null);
  }

  const blocked = preview.data ? deletionBlockedReason(preview.data) : null;
  const consequences = preview.data ? deletionConsequences(preview.data) : [];

  return (
    <>
      <Card padding="lg">
        <SettingRow
          title="Konto löschen"
          description="Löscht dein Konto sofort und endgültig — mit deinen Anlagen, Zählern, Zählerständen und Verbrauchsdaten. Wo du nur Bewohner oder Gast bist, endet dein Zugang."
          action={
            <Button variant="secondary" size="sm" onClick={start}>
              Konto löschen …
            </Button>
          }
        />
      </Card>

      <Dialog
        open={open}
        onClose={close}
        title="Konto endgültig löschen?"
        description="Das lässt sich nicht rückgängig machen. Ein Gerät, das danach wieder hinein soll, wird neu gekoppelt."
        footer={
          <>
            <Button variant="ghost" onClick={close}>
              Abbrechen
            </Button>
            <Button
              type="submit"
              form={DELETE_FORM_ID}
              variant="danger"
              disabled={remove.isPending || !preview.data || blocked !== null || password.length === 0}
            >
              Konto löschen
            </Button>
          </>
        }
      >
        <form
          id={DELETE_FORM_ID}
          onSubmit={(event) => {
            event.preventDefault();
            setError(null);
            remove.mutate();
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
            {preview.isPending && (
              <p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)" }}>Wird geprüft …</p>
            )}
            {preview.error && <Alert>{preview.error.message}</Alert>}
            {blocked !== null && <Alert>{blocked}</Alert>}
            {blocked === null && consequences.length > 0 && (
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13.5, lineHeight: 1.55, color: "var(--text-body)" }}>
                {consequences.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            )}
            {blocked === null && preview.data && (
              <Input
                label="Passwort"
                type="password"
                autoComplete="current-password"
                value={password}
                required
                onChange={(event) => setPassword(event.target.value)}
              />
            )}
          </div>

          {error !== null && (
            <div style={{ marginTop: "var(--sp-3)" }}>
              <Alert>{error}</Alert>
            </div>
          )}
        </form>
      </Dialog>
    </>
  );
}

/**
 * Eine Zeile aus Beschriftung links und Aktion rechts, wie in „Anmeldung &
 * Sicherheit" der Vorlage.
 *
 * Dort werden mehrere davon mit Haarlinien getrennt; hier gibt es bisher nur
 * eine, und einen Trenner-Schalter auf Vorrat einzubauen hieße, Code für einen
 * Fall zu schreiben, den es nicht gibt. Er kommt zurück, sobald die
 * Zwei-Faktor-Anmeldung darunter steht.
 */
function SettingRow({
  title,
  description,
  action,
}: {
  readonly title: string;
  readonly description: string;
  readonly action: ReactNode;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 12,
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <span style={{ display: "flex", flexDirection: "column", gap: 2, flex: "1 1 240px" }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: "var(--text-strong)" }}>
          {title}
        </span>
        <span style={{ fontSize: 12.5, lineHeight: 1.5, color: "var(--text-muted)" }}>
          {description}
        </span>
      </span>
      <span style={{ flex: "none" }}>{action}</span>
    </div>
  );
}

/** Beschriftung über Wert — die Feldform der Vorlage, ohne den Rahmen. */
function Fact({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
      <span style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text-body)" }}>{label}</span>
      <span
        style={{
          fontSize: 14,
          color: "var(--text-strong)",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {value}
      </span>
    </div>
  );
}

/** Ob ein Name hinterlegt ist — beide Felder sind freiwillig. */
function hasName(user: CurrentUser): boolean {
  return user.firstName !== null || user.lastName !== null;
}

/**
 * Leer heißt „nicht angegeben" und wird als `null` geschickt, nicht als `""`.
 * Der Vertrag verlangt mindestens ein Zeichen — eine leere Zeichenkette wäre
 * kein Name, sondern ein Name aus null Zeichen.
 */
function trimmedOrNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/** Verbindet den Absendeknopf im Dialogfuß mit dem Formular darüber. */
const FORM_ID = "passwort-aendern";
const OWN_SITE_FORM_ID = "eigene-anlage";
const DELETE_FORM_ID = "konto-loeschen";

/** Das Kontingent als Satz. `null` heißt unbegrenzt und nicht „keine". */
function quotaLabel(quota: number | null): string {
  if (quota === null) return "beliebig viele gleichzeitig aktiv";
  if (quota === 0) return "keine eigene";
  return quota === 1 ? "eine gleichzeitig aktiv" : `${quota} gleichzeitig aktiv`;
}
